import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { businessService } from '../../shared/services/api/businessService';

import { serviceService, productService } from '../../shared/services';
import { BusinessServicesRegister } from '../business/pages/BusinessService';
import { BusinessProductsRegister } from '../business/pages/BusinessProduct';
import { BusinessEmployees } from '../business/pages/BusinessEmployees';
import { BusinessInformation } from '../business/pages/BusinessInformation';
import { CrudListPage } from '../../shared/components/SconApp/crud/CrudListPage';
import { AlertComponent } from '../../shared/components';
import { formatDuration } from '../../shared/helpers/formatDuration';
import { OnboardingLoading } from './components/OnboardingLoading';
import { NoBusinessAvailable } from './components/NoBusinessAvailable';
import { OnboardingComplete } from './components/OnboardingComplete';
import { StepActions } from './components/StepActions';

const ONBOARDING_COMPLETE = 7;

// Ordem: Informações → Serviços → Produtos → Profissionais → WhatsApp → Publicar
const STEPS = [
  { label: 'Informações', icon: 'business' },
  { label: 'Serviços', icon: 'build' },
  { label: 'Produtos', icon: 'inventory_2' },
  { label: 'Profissionais', icon: 'people' },
  { label: 'Publicar', icon: 'public' },
];

const STEP_LABELS: Record<number, string> = {
  0: 'Informações do Negócio',
  1: 'Cadastrar Serviços',
  2: 'Cadastrar Produtos',
  3: 'Cadastrar Profissionais',
  4: 'Publicar Página',
};

const STEP_DESCRIPTIONS: Record<number, string> = {
  0: 'Revise as informações do seu estabelecimento.',
  1: 'Cadastre os serviços que você oferece.',
  2: 'Adicione os produtos que você comercializa.',
  3: 'Adicione os profissionais que trabalham no seu estabelecimento.',
  4: 'Publique sua página pública e comece a receber agendamentos online.',
};

interface IOnboardingState {
  businessId?: number;
  page?: number;
}

export const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(0);
  const stepperRef = useRef<HTMLDivElement>(null);

  const [explicitlyCompletedSteps, setExplicitlyCompletedSteps] = useState<Set<number>>(new Set());
  const [selectedOnboardingBizId, setSelectedOnboardingBizId] = useState<number | null>(null);
  const stepInitialized = useRef(false);

  const locationState = location.state as IOnboardingState | null;
  const locationBusinessId = locationState?.businessId;

  const { data: businesses, isFetching: isFetchingList } = useQuery({
    queryKey: ['businesses'],
    queryFn: () => businessService.get(),
  });

  const onboardingBusinesses = useMemo(() => {
    if (!businesses) return [];
    return businesses.filter(b => (b.onboardingStep ?? 0) < ONBOARDING_COMPLETE);
  }, [businesses]);

  const resolvedBusinessId = useMemo(() => {
    if (selectedOnboardingBizId) return selectedOnboardingBizId;
    if (locationBusinessId) return locationBusinessId;
    if (onboardingBusinesses.length === 1) return onboardingBusinesses[0].id;
    return null;
  }, [selectedOnboardingBizId, locationBusinessId, onboardingBusinesses]);

  // Reseta estado local quando troca de business
  useEffect(() => {
    setActiveStep(0);
    setExplicitlyCompletedSteps(new Set());
    stepInitialized.current = false;
  }, [resolvedBusinessId]);

  const { data: businessDetail, isLoading: isLoadingDetail } = useQuery({
    queryKey: ['business', resolvedBusinessId],
    queryFn: () => businessService.getById(resolvedBusinessId!),
    enabled: !!resolvedBusinessId,
  });

  const isFetching = isFetchingList || isLoadingDetail;

  // Initialize activeStep from backend once per business
  useEffect(() => {
    if (businessDetail && !stepInitialized.current) {
      stepInitialized.current = true;
      const onboardingStep = businessDetail.onboardingStep ?? 0;
      if (onboardingStep > 0 && onboardingStep < ONBOARDING_COMPLETE) {
        setActiveStep(Math.min(onboardingStep, STEPS.length - 1));
      }
    }
  }, [businessDetail]);

  const handleCompleteStep = (step: number) => {
    if (!resolvedBusinessId) return;
    const nextStep = step + 1;

    businessService.updateOnboardingStep(resolvedBusinessId, nextStep)
      .then(() => {
        queryClient.setQueryData(['business', resolvedBusinessId], (old) => ({
          ...(old as Record<string, unknown>),
          onboardingStep: nextStep,
        }));
      })
      .catch(() => {/* Erro silencioso — o step será re-sincronizado no próximo load */});

    if (step === 2 && !businessDetail?.products?.length) {
      setExplicitlyCompletedSteps((prev) => new Set(prev).add(step));
    }

    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const completeOnboarding = async () => {
    if (!resolvedBusinessId) return;
    await businessService.updateOnboardingStep(resolvedBusinessId, ONBOARDING_COMPLETE);
    await queryClient.invalidateQueries({ queryKey: ['business', resolvedBusinessId] });
  };

  const continueLater = () => {
    navigate('/business', { state: { businessId: resolvedBusinessId } });
  };

  const navigateToBusiness = () => {
    navigate('/business', { state: { businessId: resolvedBusinessId } });
  };

  const isStepComplete = (step: number): boolean => {
    if (!businessDetail) return false;

    if (step === 0) return !!businessDetail.name;
    if (step === 1) return (businessDetail.services?.length ?? 0) > 0;
    if (step === 2) return (businessDetail.products?.length ?? 0) > 0 || explicitlyCompletedSteps.has(2);
    if (step === 3) return (businessDetail.employees?.length ?? 0) > 0;
    if (step === 4) return (businessDetail.onboardingStep ?? 0) >= ONBOARDING_COMPLETE - 1;

    return false;
  };

  const currentStep = activeStep;
  const previousStepsComplete = [0, 1, 2, 3].every(i => isStepComplete(i));
  const noBusinessAvailable = !isFetching && !resolvedBusinessId && onboardingBusinesses.length === 0;

  // Auto-scroll stepper
  useEffect(() => {
    if (currentStep >= 3 && stepperRef.current) {
      stepperRef.current.scrollLeft = stepperRef.current.scrollWidth;
    }
  }, [currentStep]);

  if (isFetching) return <OnboardingLoading />;
  if (noBusinessAvailable) return <NoBusinessAvailable />;
  if ((businessDetail?.onboardingStep ?? 0) >= ONBOARDING_COMPLETE) {
    return <OnboardingComplete slug={businessDetail?.slug} businessId={resolvedBusinessId} />;
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 4, bgcolor: 'background.default' }}>
      <AlertComponent />
      <Container maxWidth="md">
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Bem-vindo ao SconApp! 🚀
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Complete os passos abaixo para configurar seu estabelecimento e começar a receber agendamentos.
        </Typography>

        {/* Seletor de business quando há múltiplos em onboarding */}
        {onboardingBusinesses.length > 1 && (
          <Box display='flex' alignItems='center' mb={3}>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel id='onboarding-biz-select-label'>Estabelecimento</InputLabel>
              <Select
                labelId='onboarding-biz-select-label'
                value={resolvedBusinessId ?? ''}
                label='Estabelecimento'
                onChange={(e) => {
                  setSelectedOnboardingBizId(Number(e.target.value));
                  stepInitialized.current = false;
                }}
              >
                {onboardingBusinesses.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name || `Business #${b.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Stepper */}
        <Box ref={stepperRef} sx={{ overflowX: 'auto', pb: 1, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
          <Stepper activeStep={currentStep} alternativeLabel sx={{ mb: 4, minWidth: 500 }}>
            {STEPS.map((step, index) => (
              <Step key={step.label} completed={isStepComplete(index)}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Current Step Content */}
        {currentStep < STEPS.length && (
          <Card sx={{ p: 4, borderRadius: 3, overflow: 'visible' }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Typography variant="h5" fontWeight={600}>
                {STEP_LABELS[currentStep]}
              </Typography>
              {isStepComplete(currentStep) && (
                <Chip label="Concluído" size="small" color="success" />
              )}
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {currentStep === STEPS.length - 1 && !previousStepsComplete
                ? 'Finalize os pontos pendentes para publicar sua página.'
                : STEP_DESCRIPTIONS[currentStep]
              }
            </Typography>

            {/* Step 0 — Informações (formulário inline) */}
            {currentStep === 0 && businessDetail && (
              <Box sx={{ mt: 2, width: '100%' }}>
                <BusinessInformation
                  business={businessDetail}
                  refetch={() => queryClient.invalidateQueries({ queryKey: ['business', resolvedBusinessId] })}
                />
              </Box>
            )}

            {/* Step 1 — Serviços */}
            {currentStep === 1 && (
              <Box sx={{ mt: 2 }}>
                <CrudListPage
                  items={businessDetail?.services ?? []}
                  businessId={resolvedBusinessId!}
                  entityName='service'
                  removeApi={(id) => serviceService.remove(id)}
                  onRefetch={() => queryClient.invalidateQueries({ queryKey: ['business', resolvedBusinessId] })}
                  RegisterForm={BusinessServicesRegister}
                  deleteLabel='Tem certeza que deseja deletar este serviço?'
                  newButtonLabel='Novo'
                  mapToItem={(item) => ({
                    id: item.id || 0,
                    description: `${item.description} - ${formatDuration(item.serviceDuration)} - R$${Number(item.amount).toFixed(2)}`,
                    endAdornment: (
                      <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, alignItems: 'center' }}>
                        <Chip label={item.isActive ? 'Ativo' : 'Inativo'} size='small' color={item.isActive ? 'success' : 'default'} variant={item.isActive ? 'filled' : 'outlined'} />
                        <Chip label={item.requiresPreApproval ? 'Requer aprovação' : 'Não requer aprovação'} size='small' color={item.requiresPreApproval ? 'primary' : 'default'} variant={item.requiresPreApproval ? 'filled' : 'outlined'} />
                      </Box>
                    ),
                  })}
                />
              </Box>
            )}

            {/* Step 2 — Produtos */}
            {currentStep === 2 && (
              <Box sx={{ mt: 2 }}>
                <CrudListPage
                  items={businessDetail?.products ?? []}
                  businessId={resolvedBusinessId!}
                  entityName='product'
                  removeApi={(id) => productService.remove(id)}
                  onRefetch={() => queryClient.invalidateQueries({ queryKey: ['business', resolvedBusinessId] })}
                  RegisterForm={BusinessProductsRegister}
                  deleteLabel='Tem certeza que deseja deletar este produto?'
                  newButtonLabel='Novo'
                  mapToItem={(item) => ({
                    id: item.id || 0,
                    description: `${item.description} - R$${Number(item.amount).toFixed(2)}`,
                    endAdornment: (
                      <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, alignItems: 'center' }}>
                        <Chip label={item.isActive ? 'Ativo' : 'Inativo'} size='small' color={item.isActive ? 'success' : 'default'} variant={item.isActive ? 'filled' : 'outlined'} />
                      </Box>
                    ),
                  })}
                />
              </Box>
            )}

            {/* Step 3 — Profissionais */}
            {currentStep === 3 && (
              <Box sx={{ mt: 2 }}>
                <BusinessEmployees
                  business={businessDetail!}
                  refetch={() => queryClient.invalidateQueries({ queryKey: ['business', resolvedBusinessId] })}
                  businessAdminId={0}
                />
              </Box>
            )}

            {/* Action buttons */}
            <StepActions
              currentStep={currentStep}
              isStepComplete={isStepComplete}
              previousStepsComplete={previousStepsComplete}
              onCompleteStep={handleCompleteStep}
              onCompleteOnboarding={completeOnboarding}
              onSetActiveStep={setActiveStep}
              onNavigateBusiness={navigateToBusiness}
            />
          </Card>
        )}

        {/* Progress */}
        <Box textAlign="center" mt={4}>
          <Typography variant="body2" color="text.secondary">
            {currentStep + 1} de {STEPS.length} passos
          </Typography>
          <Button
            variant="text"
            color="primary"
            onClick={continueLater}
            sx={{ mt: 1 }}
          >
            Continuar depois
          </Button>
        </Box>
      </Container>
    </Box>
  );
};
